import React, { useState, useEffect, useRef } from 'react';
import {
  CreditCard, QrCode, FileText, ArrowLeft, ChevronRight,
  Check, Copy, Loader2, AlertCircle, Lock, RefreshCw,
  ExternalLink, CheckCircle2,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────
interface UserData {
  nome: string;
  email: string;
  senha: string;
  nomeEmpresa: string;
  cargo: string;
  cpf?: string;
  celular?: string;
}

interface PlanData {
  billingPeriod: 'monthly' | 'annual';
  userCount: number;
}

interface Props {
  userData: UserData;
  planData: PlanData;
  onSuccess: (accountActivated: boolean, email: string) => void;
  onBack: () => void;
  getApiBase: () => string;
}

// ─── Helpers ───────────────────────────────────────────────────
const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

function maskCard(v: string) {
  return v.replace(/\D/g, '').substring(0, 16).replace(/(.{4})/g, '$1 ').trim();
}
function maskExpiry(v: string) {
  const d = v.replace(/\D/g, '').substring(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
}
function maskCpf(v: string) {
  const d = v.replace(/\D/g, '').substring(0, 14);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  // CPF: 000.000.000-00 (11 digits)
  if (d.length <= 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  // CNPJ: 00.000.000/0001-00 (14 digits)
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  if (d.length <= 13) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}
function maskPhone(v: string) {
  const d = v.replace(/\D/g, '').substring(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}
function maskCep(v: string) {
  const d = v.replace(/\D/g, '').substring(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

type PayMethod = 'CREDIT_CARD' | 'PIX' | 'BOLETO';

// ─── Component ─────────────────────────────────────────────────
export default function Pagamento({ userData, planData, onSuccess, onBack, getApiBase }: Props) {
  const { billingPeriod, userCount } = planData;
  const priceUnit  = billingPeriod === 'monthly' ? 80 : 72;
  const total      = priceUnit * userCount;
  const totalAnual = (billingPeriod === 'annual') ? userCount * 864 : total;
  const economy    = (billingPeriod === 'annual') ? userCount * 96 : 0;

  const [method, setMethod]         = useState<PayMethod>('CREDIT_CARD');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  // Cartão
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName]     = useState(userData.nome);
  const [expiry, setExpiry]         = useState('');
  const [ccv, setCcv]               = useState('');
  const [cpf, setCpf]               = useState(userData.cpf || '');
  const [celular, setCelular]       = useState(userData.celular || '');
  const [cep, setCep]               = useState('');
  const [numero, setNumero]         = useState('');

  // Resultado PIX/Boleto
  const [pixQrCode, setPixQrCode]   = useState('');
  const [pixImage, setPixImage]     = useState('');
  const [boletoUrl, setBoletoUrl]   = useState('');
  const [copied, setCopied]         = useState(false);
  const [waiting, setWaiting]       = useState(false);
  const [activated, setActivated]   = useState(false);

  // Polling para PIX/Boleto
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  const startPolling = () => {
    setWaiting(true);
    pollingRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${getApiBase()}/pagamento/status/${encodeURIComponent(userData.email)}`);
        const d = await r.json();
        if (d.ativo) {
          clearInterval(pollingRef.current!);
          setActivated(true);
          setWaiting(false);
          setTimeout(() => onSuccess(true, userData.email), 1500);
        }
      } catch {/* ignore */}
    }, 4000);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const body: Record<string, unknown> = {
        ...userData,
        cpf:           cpf.replace(/\D/g, ''),
        celular:       celular.replace(/\D/g, ''),
        plano:         'pro',
        billingPeriod,
        userCount,
        paymentMethod: method,
        cep:           cep.replace(/\D/g, ''),
        numero,
      };

      if (method === 'CREDIT_CARD') {
        const [em, ey] = expiry.split('/');
        body.creditCard = {
          holderName:  cardName,
          number:      cardNumber.replace(/\s/g, ''),
          expiryMonth: em,
          expiryYear:  ey ? (ey.length === 2 ? `20${ey}` : ey) : '',
          ccv,
        };
      }

      const resp = await fetch(`${getApiBase()}/pagamento/iniciar`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await resp.json();

      if (!data.success) {
        setError(data.error || 'Erro ao processar pagamento');
        return;
      }

      if (data.accountActivated) {
        // Cartão aprovado → conta ativa na hora
        onSuccess(true, userData.email);
      } else if (method === 'PIX') {
        setPixQrCode(data.paymentInfo?.pixQrCode || '');
        setPixImage(data.paymentInfo?.pixImage || '');
        startPolling();
      } else if (method === 'BOLETO') {
        setBoletoUrl(data.paymentInfo?.bankSlipUrl || '');
        startPolling();
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setPixQrCode('');
    setPixImage('');
    setBoletoUrl('');
    setWaiting(false);
    setActivated(false);
    setError('');
  };

  // ── Se PIX foi emitido ────────────────────────────────────
  if (pixQrCode || pixImage) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-7 shadow-sm">
        <div className="text-center mb-6">
          {activated ? (
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
          ) : (
            <div className="w-14 h-14 mx-auto rounded-full bg-indigo-50 flex items-center justify-center mb-3">
              <QrCode className="w-7 h-7 text-indigo-600" />
            </div>
          )}
          <h3 className="text-lg font-bold text-gray-900">
            {activated ? 'Pagamento confirmado! 🎉' : 'Escaneie o QR Code'}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {activated
              ? 'Sua conta está sendo ativada...'
              : 'Use o app do seu banco para pagar via PIX'}
          </p>
        </div>

        {pixImage && (
          <div className="flex justify-center mb-4">
            <img
              src={`data:image/png;base64,${pixImage}`}
              alt="QR Code PIX"
              className="w-48 h-48 rounded-xl border border-gray-200"
            />
          </div>
        )}

        {pixQrCode && (
          <div className="mb-5">
            <p className="text-xs text-gray-500 mb-1.5 font-medium">PIX Copia e Cola</p>
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3 border border-gray-200">
              <p className="text-xs text-gray-700 flex-1 font-mono truncate">{pixQrCode}</p>
              <button
                onClick={() => handleCopy(pixQrCode)}
                className="shrink-0 p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
          {waiting && !activated && (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
              <span>Aguardando confirmação do pagamento...</span>
            </>
          )}
          {activated && (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
              <span className="text-emerald-600 font-medium">Ativando sua conta...</span>
            </>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-5">
          Total: <strong className="text-gray-700">R$ {fmt(billingPeriod === 'annual' ? totalAnual : total)}</strong>
          {billingPeriod === 'annual' ? '/ano' : '/mês'}
        </p>

        {!activated && (
          <button
            onClick={handleReset}
            className="mt-4 w-full text-sm text-gray-500 hover:text-gray-900 font-medium transition-colors flex items-center justify-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar e alterar forma de pagamento
          </button>
        )}
      </div>
    );
  }

  // ── Se Boleto foi emitido ─────────────────────────────────
  if (boletoUrl) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-7 shadow-sm">
        <div className="text-center mb-6">
          {activated ? (
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
          ) : (
            <div className="w-14 h-14 mx-auto rounded-full bg-indigo-50 flex items-center justify-center mb-3">
              <FileText className="w-7 h-7 text-indigo-600" />
            </div>
          )}
          <h3 className="text-lg font-bold text-gray-900">
            {activated ? 'Pagamento confirmado! 🎉' : 'Boleto gerado!'}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {activated ? 'Ativando sua conta...' : 'O boleto vence em 3 dias úteis'}
          </p>
        </div>

        {!activated && (
          <>
            <a
              href={boletoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all mb-4"
            >
              <ExternalLink className="w-4 h-4" /> Abrir / Imprimir Boleto
            </a>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-700 space-y-1">
              <p className="font-semibold">⚠️ Importante</p>
              <p>• Após o pagamento, a confirmação pode levar até 3 dias úteis.</p>
              <p>• Você receberá um e-mail quando sua conta for ativada.</p>
              <p>• Boletos pagos aos finais de semana são compensados na segunda-feira.</p>
            </div>
          </>
        )}

        <div className="flex items-center justify-center gap-2 text-sm mt-5">
          {waiting && !activated && (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-indigo-500" />
              <span className="text-gray-500">Verificando confirmação...</span>
            </>
          )}
          {activated && (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
              <span className="text-emerald-600 font-medium">Ativando sua conta...</span>
            </>
          )}
        </div>

        {!activated && (
          <button
            onClick={handleReset}
            className="mt-4 w-full text-sm text-gray-500 hover:text-gray-900 font-medium transition-colors flex items-center justify-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar e alterar forma de pagamento
          </button>
        )}
      </div>
    );
  }

  // ── Formulário principal ──────────────────────────────────
  const methods: { id: PayMethod; label: string; Icon: React.ElementType }[] = [
    { id: 'CREDIT_CARD', label: 'Cartão',  Icon: CreditCard },
    { id: 'PIX',         label: 'PIX',     Icon: QrCode },
    { id: 'BOLETO',      label: 'Boleto',  Icon: FileText },
  ];

  const isCardValid = method !== 'CREDIT_CARD' || (
    cardNumber.replace(/\s/g, '').length === 16 &&
    cardName.trim().length > 2 &&
    expiry.length === 5 &&
    ccv.length >= 3 &&
    [11, 14].includes(cpf.replace(/\D/g, '').length)
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-7 shadow-sm">
      {/* Resumo do plano */}
      <div className="bg-indigo-50 rounded-xl p-4 mb-6 border border-indigo-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Plano Pro · {billingPeriod === 'monthly' ? 'Mensal' : 'Anual'}</p>
            <p className="text-xs text-gray-500">{userCount} usuário{userCount > 1 ? 's' : ''} × R$ {priceUnit}/mês</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-extrabold text-indigo-600">
              R$ {fmt(billingPeriod === 'annual' ? totalAnual : total)}
              <span className="text-xs font-semibold text-gray-400">{billingPeriod === 'annual' ? '/ano' : '/mês'}</span>
            </p>
            {economy > 0 && (
              <p className="text-[11px] text-emerald-600 font-semibold">Economia de R$ {fmt(economy)}/ano</p>
            )}
          </div>
        </div>
      </div>

      {/* Seletor de método */}
      <div className="grid grid-cols-3 gap-2 mb-6">
        {methods.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setMethod(id)}
            className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-xs font-semibold transition-all ${
              method === id
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <Icon className="w-5 h-5" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Formulário Cartão ── */}
      {method === 'CREDIT_CARD' && (
        <div className="space-y-4">
          {/* Número do Cartão */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Número do cartão</label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                inputMode="numeric"
                value={cardNumber}
                maxLength={19}
                onChange={e => setCardNumber(maskCard(e.target.value))}
                placeholder="0000 0000 0000 0000"
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none font-mono tracking-widest"
              />
            </div>
          </div>

          {/* Nome no cartão */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nome (igual ao cartão)</label>
            <input
              type="text"
              value={cardName}
              onChange={e => setCardName(e.target.value.toUpperCase())}
              placeholder="NOME COMPLETO"
              className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none uppercase tracking-wide"
            />
          </div>

          {/* Validade + CVC */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Validade</label>
              <input
                type="text"
                inputMode="numeric"
                value={expiry}
                maxLength={5}
                onChange={e => setExpiry(maskExpiry(e.target.value))}
                placeholder="MM/AA"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">CVC</label>
              <input
                type="text"
                inputMode="numeric"
                value={ccv}
                maxLength={4}
                onChange={e => setCcv(e.target.value.replace(/\D/g, ''))}
                placeholder="000"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none font-mono"
              />
            </div>
          </div>

          {/* CPF/CNPJ do titular */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">CPF / CNPJ do titular</label>
            <input
              type="text"
              inputMode="numeric"
              value={cpf}
              maxLength={18}
              onChange={e => setCpf(maskCpf(e.target.value))}
              placeholder="000.000.000-00 ou 00.000.000/0001-00"
              className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
            />
          </div>

          {/* CEP + Número */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">CEP</label>
              <input
                type="text"
                inputMode="numeric"
                value={cep}
                maxLength={9}
                onChange={e => setCep(maskCep(e.target.value))}
                placeholder="00000-000"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nº residencial</label>
              <input
                type="text"
                value={numero}
                onChange={e => setNumero(e.target.value)}
                placeholder="123"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Info PIX ── */}
      {method === 'PIX' && (
        <div className="space-y-4">
          {/* CPF/CNPJ + celular para criar customer no Asaas */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">CPF / CNPJ</label>
              <input
                type="text"
                inputMode="numeric"
                value={cpf}
                maxLength={18}
                onChange={e => setCpf(maskCpf(e.target.value))}
                placeholder="CPF ou CNPJ"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Celular</label>
              <input
                type="text"
                inputMode="tel"
                value={celular}
                maxLength={15}
                onChange={e => setCelular(maskPhone(e.target.value))}
                placeholder="(11) 99999-9999"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
              />
            </div>
          </div>
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100 text-sm text-indigo-700 space-y-1.5">
            <p className="font-semibold flex items-center gap-2"><QrCode className="w-4 h-4" /> Como funciona</p>
            <p className="text-xs text-indigo-600">1. Clique em "Gerar QR Code"</p>
            <p className="text-xs text-indigo-600">2. Escaneie com o app do seu banco</p>
            <p className="text-xs text-indigo-600">3. Confirme o pagamento — sua conta é ativada na hora!</p>
          </div>
        </div>
      )}

      {/* ── Info Boleto ── */}
      {method === 'BOLETO' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">CPF / CNPJ</label>
              <input
                type="text"
                inputMode="numeric"
                value={cpf}
                maxLength={18}
                onChange={e => setCpf(maskCpf(e.target.value))}
                placeholder="CPF ou CNPJ"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Celular</label>
              <input
                type="text"
                inputMode="tel"
                value={celular}
                maxLength={15}
                onChange={e => setCelular(maskPhone(e.target.value))}
                placeholder="(11) 99999-9999"
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
              />
            </div>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-sm text-amber-700 space-y-1.5">
            <p className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> Boleto bancário</p>
            <p className="text-xs">• Vencimento em 3 dias úteis</p>
            <p className="text-xs">• Confirmação em até 3 dias após o pagamento</p>
            <p className="text-xs">• Conta liberada automaticamente após compensação</p>
          </div>
        </div>
      )}

      {/* Erro */}
      {error && (
        <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Botão pagar */}
      <button
        onClick={handleSubmit}
        disabled={loading || !isCardValid}
        className="mt-6 w-full py-3.5 px-4 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-sm"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Processando...</>
        ) : (
          <>
            <Lock className="w-4 h-4" />
            {method === 'CREDIT_CARD' && `Pagar R$ ${fmt(billingPeriod === 'annual' ? totalAnual : total)}`}
            {method === 'PIX'         && 'Gerar QR Code PIX'}
            {method === 'BOLETO'      && 'Gerar Boleto'}
            <ChevronRight className="w-4 h-4" />
          </>
        )}
      </button>

      {/* Segurança */}
      <p className="text-center text-[11px] text-gray-400 mt-3 flex items-center justify-center gap-1">
        <Lock className="w-3 h-3" /> Pagamento processado com segurança pelo Asaas
      </p>

      {/* Voltar */}
      <button
        onClick={onBack}
        className="mt-4 w-full text-sm text-gray-500 hover:text-gray-900 font-medium transition-colors flex items-center justify-center gap-1.5"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar e escolher outro plano
      </button>
    </div>
  );
}
